import { useEffect, useRef } from 'react'
import { BottomSheet } from './BottomSheet.jsx'
import { AnchoredPopover } from './AnchoredPopover.jsx'
import { useIsDesktop } from '../useIsDesktop.js'

const ITEM_H = 40 // px — altezza di ogni item della wheel (deve combaciare con CSS)

// Time picker stile "wheel": due colonne scrollabili (HH, MM). L'item al
// centro è quello selezionato (auto-update on scroll). Tap su un item lo
// porta al centro. Il separatore `:` è un overlay statico al centro.
export function TimePicker({ value, onChange, onClose, open, t, anchorRef }) {
  const isDesktop = useIsDesktop()
  const [hh, mm] = parseTime(value)

  function commit(newHh, newMm) {
    onChange(`${pad(newHh)}:${pad(newMm)}`)
  }

  function resetToNow() {
    const d = new Date()
    onChange(`${pad(d.getHours())}:${pad(d.getMinutes())}`)
    onClose()
  }

  const content = (
    <>
      <div className="tp-header">
        <span className="tp-title">{t.transactions.time}</span>
        <button type="button" className="tp-reset" onClick={resetToNow}>
          {t.transactions.now}
        </button>
        <button type="button" className="tp-close" onClick={onClose} aria-label={t.common.close}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="tp-columns">
        <Wheel length={24} value={hh} onChange={(v) => commit(v, mm)} open={open} />
        <Wheel length={60} value={mm} onChange={(v) => commit(hh, v)} open={open} />
        <div className="tp-center" aria-hidden="true">
          <span className="tp-sep">:</span>
        </div>
      </div>
    </>
  )

  if (isDesktop && anchorRef) {
    return (
      <AnchoredPopover open={open} onClose={onClose} anchorRef={anchorRef} width={200} className="tp-popover">
        {content}
      </AnchoredPopover>
    )
  }
  return (
    <BottomSheet open={open} onClose={onClose} className="tp-sheet">
      {content}
    </BottomSheet>
  )
}

function Wheel({ length, value, onChange, open }) {
  const ref = useRef(null)
  // Track last value applied via scroll, to avoid feedback loops
  const lastEmitted = useRef(value)
  const scrollTimer = useRef(null)

  // Apply top/bottom padding equal to (containerH - ITEM_H)/2 so that the
  // first / last items can be scrolled to the centre.
  useEffect(() => {
    if (!open || !ref.current) return
    const el = ref.current
    function applyPadding() {
      const pad = Math.max(0, (el.clientHeight - ITEM_H) / 2)
      el.style.paddingTop    = `${pad}px`
      el.style.paddingBottom = `${pad}px`
    }
    applyPadding()
    window.addEventListener('resize', applyPadding)
    return () => window.removeEventListener('resize', applyPadding)
  }, [open])

  // When `open` flips on, jump to the current value (no animation)
  useEffect(() => {
    if (!open || !ref.current) return
    // Use a microtask so padding is in place
    const id = requestAnimationFrame(() => {
      if (!ref.current) return
      ref.current.scrollTop = value * ITEM_H
      lastEmitted.current = value
    })
    return () => cancelAnimationFrame(id)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // When `value` changes externally (e.g. parent re-init), snap to it
  useEffect(() => {
    if (!open || !ref.current) return
    if (value === lastEmitted.current) return
    ref.current.scrollTo({ top: value * ITEM_H, behavior: 'smooth' })
    lastEmitted.current = value
  }, [value, open])

  function onScroll() {
    if (!ref.current) return
    // Debounce slightly so we update only after the scroll settles
    clearTimeout(scrollTimer.current)
    scrollTimer.current = setTimeout(() => {
      if (!ref.current) return
      const idx = Math.round(ref.current.scrollTop / ITEM_H)
      const clamped = Math.max(0, Math.min(length - 1, idx))
      if (clamped !== lastEmitted.current) {
        lastEmitted.current = clamped
        onChange(clamped)
      }
    }, 60)
  }

  function pickItem(i) {
    if (!ref.current) return
    ref.current.scrollTo({ top: i * ITEM_H, behavior: 'smooth' })
  }

  const items = []
  for (let i = 0; i < length; i++) items.push(i)

  return (
    <div className="tp-col" ref={ref} onScroll={onScroll}>
      {items.map(i => (
        <button
          key={i}
          type="button"
          className={`tp-item${i === value ? ' selected' : ''}`}
          onClick={() => pickItem(i)}
        >{pad(i)}</button>
      ))}
    </div>
  )
}

function parseTime(s) {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s ?? '')
  if (!m) return [0, 0]
  return [Math.max(0, Math.min(23, parseInt(m[1], 10))),
          Math.max(0, Math.min(59, parseInt(m[2], 10)))]
}
function pad(n) { return String(n).padStart(2, '0') }
